import { useLatest } from 'ahooks';
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useRef, useState } from 'react';
import { IFramework, trackConfig } from '@/framework';
import { BackgroundLayer } from '@/layers/background-layer';
import CropImageScale from '@/plugins/crop-img/components/crop-image-scale/crop-image-scale';
import { adjustCropLayer } from '@/utils/crop-img-kit';
import { IBackgroundLayerProps, ILayerProps, LayerType } from '@/version-types';
import { CROP_BACKGROUND_EVENTS } from '../event-name';
import CropBackgroundOperate from './crop-background-operate';

const CropBackgroundWrap: React.FC<{
    layer: ILayerProps;
    scale: number;
    stageWidth: number;
    stageHeight: number;
    framework: IFramework;
}> = ({ layer: activeLayer, scale, stageWidth, stageHeight, framework }) => {
    const originLayerRef = useRef(cloneDeep(activeLayer));
    const [isChange, setIsChange] = useState(false);

    useEffect(() => {
        if (isChange || !activeLayer) {
            return;
        }

        if (JSON.stringify(activeLayer) !== JSON.stringify(originLayerRef.current)) {
            setIsChange(true);
        }
    }, [activeLayer, isChange]);

    const { imageTransform } = activeLayer.data as IBackgroundLayerProps;
    const { scale: imageScale } = imageTransform ?? {};

    const setImageScale = (scale: number, isEnd?: boolean) => {
        imageTransform.scale = scale > 3 ? 3 : scale < 1 ? 1 : scale;

        if (isEnd) {
            adjustCropLayer(activeLayer);
        }
        framework.stage.addOrUpdate({ ...activeLayer });
    };

    const checkSyncLayer = (layer: ILayerProps) => {
        framework.history.realEdit.sendLayer(layer);
    };

    const layerIdRef = useLatest(activeLayer.id);
    useEffect(() => {
        // 激活舞台变化时，取消裁剪
        const handler = framework.events.onActiveStageChange(() => {
            const layer = framework.getLayer(layerIdRef.current || '', true);
            if (layer && layer.__isCropImage) {
                cancelCropImage();
            }
        });
        return () => {
            framework.events.off(handler);
        };
    }, []);

    const cancelCropImage = () => {
        const layer = cloneDeep(originLayerRef.current);
        layer.__isCropImage = false;
        framework.stage.addOrUpdate(layer);
        framework.operate.enablePick();
        framework.events.emit(CROP_BACKGROUND_EVENTS.CROP_CANCEL);
        checkSyncLayer(layer);
        framework.events.emit(CROP_BACKGROUND_EVENTS.CROP_CLOSE);
    };

    const confirmCropImage = () => {
        framework.operate.enablePick();
        const layer = cloneDeep(activeLayer);
        layer.__isCropImage = false;
        framework.getInstance(BackgroundLayer).update(layer, true, trackConfig[LayerType.BACKGROUND_LAYER]?.crop, {
            ...originLayerRef.current,
            __isCropImage: false,
        });
        framework.events.emit(CROP_BACKGROUND_EVENTS.CROP_CONFIRM);
        checkSyncLayer(layer);
        framework.events.emit(CROP_BACKGROUND_EVENTS.CROP_CLOSE);
    };

    const resetCropImage = () => {
        framework.stage.addOrUpdate(cloneDeep(originLayerRef.current));
    };

    if (!activeLayer.__isCropImage) {
        return null;
    }

    return (
        <>
            <CropBackgroundOperate
                scale={scale}
                layer={activeLayer}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
                framework={framework}
            />

            <CropImageScale
                scale={imageScale}
                setScale={setImageScale}
                cancel={cancelCropImage}
                confirm={confirmCropImage}
                reset={resetCropImage}
                isChange={isChange}
                isBackground={true}
            />
        </>
    );
};

export default CropBackgroundWrap;
